import './ClientHealth.scss';
import cn from 'classnames';
import { ReactNode, UIEvent, useEffect, useRef, useState } from 'react';
import { IoRocketSharp } from 'react-icons/io5';
import { MdCalendarToday, MdClose, MdFlag } from 'react-icons/md';
import { RiExternalLinkLine, RiHeartPulseLine } from 'react-icons/ri';
import { Bar, BarChart, ResponsiveContainer, Tooltip } from 'recharts';
import { SignalKind } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import {
  ClientHealthBandCounts,
  ClientHealthBands,
  ClientHealthRange,
  ClientHealthSignal,
  GetClientHealthCountsResponse
} from '@blisspointmedia/bpm-types/dist/OracleApi';
import { ChartContainer } from '@Components/ChartContainer';
import { CHART_PRESET, ChartXAxis } from '@Components/Charts/chart-presets';
import { ChartTooltip } from '@Components/Charts/ChartTooltip/ChartTooltip';
import { Appearance, Background, Design, Size } from '@Components/designConstants';
import { Dropdown, DropdownToggleType } from '@Components/Dropdown';
import { Button } from '@Components/Button';
import { InfoTooltip } from '@Components/InfoTooltip';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Spinner } from '@Components/Spinner';
import { WidgetContainer } from '@Components/WidgetContainer';
import { CLIENT_HEALTH_HINT } from '@src/Pages/AgenticPlatform/pages/ClientHome/client-home-constants';
import { useSetError } from '@src/redux/modals';
import {
  BAND_LABELS,
  CLIENT_HEALTH_RANGE_OPTIONS,
  CLIENT_HEALTH_SERIES,
  ClientHealthSeries,
  DEFAULT_CLIENT_HEALTH_RANGE,
  RISK_FILTER_OPTIONS,
  RISK_FILTER_ALL,
  RiskFilterType,
  SignalSelection,
  WeeklySignalRow
} from './client-health-constants';
import {
  countsFor,
  activityFeedEventPath,
  activityFeedSignalPath,
  formatSignalDate,
  isHighlighted,
  isNearListEnd,
  isSameSelection,
  selectionLabel,
  signalsFilterFor,
  hiddenCountFor,
  toWeeklySignalRows
} from './client-health-utils';
import { useClientHealthCounts } from './useClientHealthCounts';
import {
  ClientHealthSignalsResult,
  useClientHealthSignals,
  usePrefetchClientHealthSignals
} from './useClientHealthSignals';

interface ClientHealthProps {
  cid: string;
}

export const ClientHealth = ({ cid }: ClientHealthProps) => {
  const [range, setRange] = useState<ClientHealthRange>(DEFAULT_CLIENT_HEALTH_RANGE);
  const { data, isLoading, error } = useClientHealthCounts(cid, range);
  usePrefetchClientHealthSignals(cid, range);
  const setError = useSetError();

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load Client Health",
        message: `Failed to load Client Health: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  return (
    <WidgetContainer
      className='client-health'
      dataTestId='client-health'
      background={Background.DARK}
      header={
        <div className='client-health-header'>
          <RiHeartPulseLine className='client-health-header-icon' />
          Client Health
          <InfoTooltip size={Size.SM} design={Design.NEUTRAL}>
            {CLIENT_HEALTH_HINT}
          </InfoTooltip>
        </div>
      }
      rightActions={
        <Dropdown
          ariaLabel='Date range'
          size={Size.SM}
          leadingIcon={<MdCalendarToday />}
          value={range}
          options={CLIENT_HEALTH_RANGE_OPTIONS}
          onChange={setRange}
        />
      }
    >
      {isLoading && <Spinner />}
      {data && <ClientHealthBody key={`${cid}:${range}`} cid={cid} range={range} data={data} />}
    </WidgetContainer>
  );
};

const useSignalsErrorModal = (error: Error | null) => {
  const setError = useSetError();
  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load signals",
        message: `Failed to load Client Health signals: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);
};

interface ClientHealthBodyProps {
  cid: string;
  range: ClientHealthRange;
  data: GetClientHealthCountsResponse;
}

const ClientHealthBody = ({ cid, range, data }: ClientHealthBodyProps) => {
  const [riskFilter, setRiskFilter] = useState<RiskFilterType>(RISK_FILTER_ALL);
  const [selection, setSelection] = useState<SignalSelection | null>(null);
  const positives = useClientHealthSignals(
    cid,
    range,
    SignalKind.POSITIVE,
    signalsFilterFor(SignalKind.POSITIVE, selection, riskFilter)
  );
  const risks = useClientHealthSignals(
    cid,
    range,
    SignalKind.RISK,
    signalsFilterFor(SignalKind.RISK, selection, riskFilter)
  );
  useSignalsErrorModal(positives.error);
  useSignalsErrorModal(risks.error);

  const toggleSelection = (next: SignalSelection) =>
    setSelection(current => (isSameSelection(current, next) ? null : next));
  const clearSelection = () => setSelection(null);

  return (
    <div className='client-health-body'>
      <div className='client-health-columns'>
        <SignalColumn
          title='Positive Signals'
          signal={SignalKind.POSITIVE}
          counts={countsFor(data.counts, SignalKind.POSITIVE, riskFilter)}
          paging={positives}
          hiddenCount={hiddenCountFor(data.hidden, SignalKind.POSITIVE, riskFilter)}
          feedPath={activityFeedSignalPath(cid, SignalKind.POSITIVE)}
          cid={cid}
          selection={selection}
          onSelect={toggleSelection}
          onClearSelection={clearSelection}
        />
        <SignalColumn
          title='Risk Signals'
          signal={SignalKind.RISK}
          counts={countsFor(data.counts, SignalKind.RISK, riskFilter)}
          feedPath={activityFeedSignalPath(cid, SignalKind.RISK)}
          cid={cid}
          paging={risks}
          hiddenCount={hiddenCountFor(data.hidden, SignalKind.RISK, riskFilter)}
          selection={selection}
          onSelect={toggleSelection}
          onClearSelection={clearSelection}
        >
          <Dropdown
            ariaLabel='Risk type'
            type={DropdownToggleType.WIDGET_TITLE}
            value={riskFilter}
            options={RISK_FILTER_OPTIONS}
            onChange={setRiskFilter}
          />
        </SignalColumn>
      </div>
      <SignalsByWeekChart
        rows={toWeeklySignalRows(data.weeks, riskFilter, selection)}
        selection={selection}
        onSelect={toggleSelection}
        onClearSelection={clearSelection}
      />
    </div>
  );
};

interface SignalColumnProps {
  title: string;
  signal: SignalKind;
  counts: ClientHealthBandCounts;
  paging: ClientHealthSignalsResult;
  feedPath: string;
  cid: string;
  hiddenCount?: number;
  selection: SignalSelection | null;
  onSelect: (selection: SignalSelection) => void;
  onClearSelection: () => void;
  children?: ReactNode;
}

const SignalColumn = ({
  title,
  signal,
  counts,
  paging,
  feedPath,
  cid,
  hiddenCount,
  selection,
  onSelect,
  onClearSelection,
  children
}: SignalColumnProps) => {
  const columnSelection = selection?.signal === signal ? selection : null;
  return (
    <div className={cn('client-health-column', signal)} data-testid={`client-health-${signal}`}>
      <div className='client-health-column-header'>
        {signal === SignalKind.POSITIVE ? (
          <IoRocketSharp className='client-health-column-icon' size={28} />
        ) : (
          <MdFlag className='client-health-column-icon' size={28} />
        )}
        <div className='client-health-column-heading'>
          {children}
          <h3 className='client-health-column-title'>{title}</h3>
        </div>
      </div>
      <div className='client-health-counts'>
        {ClientHealthBands.map(band => (
          <InteractiveDiv
            key={band}
            className={cn('client-health-count', band, {
              dimmed: !isHighlighted(selection, signal, band)
            })}
            aria-pressed={columnSelection?.band === band}
            onClick={() => onSelect({ signal, band })}
          >
            <span className='client-health-count-value'>{counts[band]}</span>
            <span className='client-health-count-label'>{BAND_LABELS[band]}</span>
          </InteractiveDiv>
        ))}
        {Boolean(hiddenCount) && (
          <span className='client-health-hidden' data-testid='client-health-hidden'>
            {hiddenCount} Hidden
          </span>
        )}
      </div>
      <SignalList
        signals={!selection || columnSelection ? paging.signals : []}
        paging={paging}
        cid={cid}
        canPage={!selection || Boolean(columnSelection)}
        emptyContent={
          selection ? (
            <div className='client-health-empty-filter'>
              <ClearFilter selection={selection} onClearSelection={onClearSelection} />
            </div>
          ) : (
            <p className='client-health-empty'>No signals in this range.</p>
          )
        }
      />
      <a
        className='client-health-feed-link'
        href={feedPath}
        target='_blank'
        rel='noopener noreferrer'
      >
        Open in feed
        <RiExternalLinkLine />
      </a>
    </div>
  );
};

interface ClearFilterProps {
  selection: SignalSelection;
  onClearSelection: () => void;
}

const ClearFilter = ({ selection, onClearSelection }: ClearFilterProps) => (
  <Button
    size={Size.SM}
    appearance={Appearance.EMPTY}
    design={Design.NEUTRAL}
    trailingElement={<MdClose />}
    title='Clear filter'
    data-testid='client-health-filter'
    onClick={onClearSelection}
  >
    {selectionLabel(selection)}
  </Button>
);

interface SignalListProps {
  signals: ClientHealthSignal[];
  paging: ClientHealthSignalsResult;
  cid: string;
  // False while a filter on the other column empties this list, so it doesn't fetch every page.
  canPage: boolean;
  emptyContent: ReactNode;
}

const SignalList = ({ signals, paging, cid, canPage, emptyContent }: SignalListProps) => {
  const listRef = useRef<HTMLUListElement>(null);
  const { isLoading, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = paging;
  const hasNextPage = canPage && paging.hasNextPage;
  // A failed page keeps hasNextPage true, so without the error check the effect retries forever.
  const canLoadMore = hasNextPage && !isFetchingNextPage && !isFetchNextPageError;

  // A filtered or short list may not overflow, so no scroll event would ever load the next page.
  useEffect(() => {
    const el = listRef.current;
    if (canLoadMore && (!el || isNearListEnd(el))) {
      fetchNextPage();
    }
  }, [canLoadMore, signals.length, fetchNextPage]);

  const handleScroll = (event: UIEvent<HTMLUListElement>) => {
    if (canLoadMore && isNearListEnd(event.currentTarget)) {
      fetchNextPage();
    }
  };

  if (isLoading) return <Spinner />;
  if (signals.length === 0 && !hasNextPage) {
    return emptyContent;
  }
  return (
    <ul className='client-health-list' ref={listRef} onScroll={handleScroll}>
      {signals.map(item => (
        <li key={item.id}>
          <a
            className='client-health-row'
            href={activityFeedEventPath(cid, item.id)}
            target='_blank'
            rel='noopener noreferrer'
            title={item.title}
          >
            <span className={cn('client-health-row-dot', item.importance)} />
            <span className='client-health-row-title'>{item.title}</span>
            <span className='client-health-row-date'>
              <MdCalendarToday size={16} />
              {formatSignalDate(item.date)}
            </span>
          </a>
        </li>
      ))}
      {isFetchingNextPage && (
        <li className='client-health-list-loading'>
          <Spinner />
        </li>
      )}
      {isFetchNextPageError && !isFetchingNextPage && (
        <li className='client-health-list-loading'>
          <Button
            size={Size.SM}
            appearance={Appearance.EMPTY}
            data-testid='client-health-retry'
            onClick={() => fetchNextPage()}
          >
            Retry
          </Button>
        </li>
      )}
    </ul>
  );
};

interface SignalsByWeekChartProps {
  rows: WeeklySignalRow[];
  selection: SignalSelection | null;
  onSelect: (selection: SignalSelection) => void;
  onClearSelection: () => void;
}

const SignalsByWeekChart = ({
  rows,
  selection,
  onSelect,
  onClearSelection
}: SignalsByWeekChartProps) => {
  const visibleSeries = CLIENT_HEALTH_SERIES.filter(series =>
    isHighlighted(selection, series.signal, series.band)
  );
  return (
    <ChartContainer title='New Signals Each Week' className='client-health-chart'>
      <div className='client-health-chart-plot' data-testid='client-health-chart'>
        <ResponsiveContainer height='100%'>
          <BarChart
            data={rows}
            margin={CHART_PRESET.margin}
            barCategoryGap='20%'
            onClick={onClearSelection}
          >
            <ChartXAxis dataKey='label' />
            <Tooltip
              content={<WeekTooltip series={visibleSeries} />}
              cursor={false}
              // A full bar width from the pointer, so the tooltip never covers the hovered bar.
              offset={CHART_PRESET.barMaxWidth}
            />
            {/* Every series stays mounted: recharts 3 restacks by mount order, so remounting a
                filtered-out Bar moves it to the top. Unselected series are zeroed in `rows`. */}
            {CLIENT_HEALTH_SERIES.map(series => (
              <Bar
                key={series.dataKey}
                dataKey={series.dataKey}
                stackId='signals'
                fill={series.color}
                maxBarSize={CHART_PRESET.barMaxWidth}
                isAnimationActive={false}
                cursor='pointer'
                onClick={(_bar, _index, event) => {
                  // Keeps the chart's background click, which clears the filter, from also firing.
                  event.stopPropagation();
                  onSelect({ signal: series.signal, band: series.band });
                }}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartContainer>
  );
};

interface WeekTooltipProps {
  series: readonly ClientHealthSeries[];
  active?: boolean;
  payload?: { payload: WeeklySignalRow }[];
}

const WeekTooltip = ({ series: visibleSeries, active, payload }: WeekTooltipProps) => {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  return (
    <ChartTooltip
      headerLabel={`Week of ${row.label}`}
      reverseItems
      items={[
        ...visibleSeries.map(series => ({
          label: series.label,
          value: row[series.dataKey],
          color: series.color
        })),
        { label: 'Total', value: row.total, color: undefined }
      ]}
    />
  );
};
